import React, { Component, ErrorInfo, ReactNode } from 'react';
import errorTracker from '../../utils/errorTracking';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error to tracking service
    errorTracker.logError(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI (styles: styles/components/feedback.css — plain CSS, no Tailwind)
      return (
        <div className="error-screen" role="alert">
          <div className="error-screen__card">
            <div className="error-screen__icon" aria-hidden="true">⚠️</div>
            <h1 className="error-screen__title">Something went wrong</h1>
            <p className="error-screen__body">
              This screen hit an unexpected error. Your positions and saved data are not
              affected. Refreshing the page usually fixes it.
            </p>
            <div className="error-screen__actions">
              <button type="button" className="btn-primary" onClick={() => window.location.reload()}>
                Refresh page
              </button>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  this.setState({ hasError: false, error: null });
                }}
              >
                Try again
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="error-screen__details">
                <summary>Error details (development only)</summary>
                <pre>
                  {this.state.error.toString()}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;